/**
 * Production Vein Accord.
 *
 * Uses the Daily Performance Pool genealogy: each leg is one direct referral
 * branch of a leader who already has an active pool team. Paid shares only.
 * Counting starts 1 October 2026. The end follows the Production Capital phase.
 *
 * This module calculates qualification. It does not credit balances or shares.
 */

import {
  PRODUCTION_CAPITAL_CAMPAIGN,
  isProductionCapitalCampaignActive,
} from './productionCapitalCampaign.js'

/** Live test account. Everyone else is refused by the API and hidden in the menu. */
export const GLOBAL_TOP_AFFILIATE_POOL_TEST_USERNAME = 'rrpaswan'

/** 1 October 2026 00:00 SAST (UTC+2). */
export const GLOBAL_TOP_AFFILIATE_POOL_START = '2026-09-30T22:00:00.000Z'

export function isGlobalTopAffiliatePoolTester(username) {
  return String(username || '').trim().toLowerCase() === GLOBAL_TOP_AFFILIATE_POOL_TEST_USERNAME
}

export const VEIN_ACCORD_LEVELS = Object.freeze([
  { level: 1, minShares: 500, rate: 0.005, minLegs: 2, maxLegShare: 0.80, minLegOfLevel: 0 },
  { level: 2, minShares: 1000, rate: 0.01, minLegs: 2, maxLegShare: 0.75, minLegOfLevel: 0 },
  { level: 3, minShares: 2500, rate: 0.02, minLegs: 2, maxLegShare: 0.70, minLegOfLevel: 0 },
  { level: 4, minShares: 5000, rate: 0.03, minLegs: 2, maxLegShare: 0.65, minLegOfLevel: 0 },
  { level: 5, minShares: 10000, rate: 0.05, minLegs: 3, maxLegShare: 0.60, minLegOfLevel: 0.10 },
  { level: 6, minShares: 15000, rate: 0.06, minLegs: 3, maxLegShare: 0.60, minLegOfLevel: 0.10 },
  { level: 7, minShares: 20000, rate: 0.07, minLegs: 3, maxLegShare: 0.55, minLegOfLevel: 0.10 },
  { level: 8, minShares: 25000, rate: 0.08, minLegs: 3, maxLegShare: 0.55, minLegOfLevel: 0.10 },
  { level: 9, minShares: 30000, rate: 0.10, minLegs: 3, maxLegShare: 0.50, minLegOfLevel: 0.10 },
])

export function veinAccordWindow(now = new Date(), paidSharesSold = 0) {
  const start = GLOBAL_TOP_AFFILIATE_POOL_START
  const end = PRODUCTION_CAPITAL_CAMPAIGN.endDate
  const t = now.getTime()
  const phaseOpen = isProductionCapitalCampaignActive({ paidSharesSold, now })
  const open = t >= Date.parse(start) && t <= Date.parse(end) && phaseOpen
  return {
    open,
    start,
    end,
    phaseName: PRODUCTION_CAPITAL_CAMPAIGN.name,
    campaignId: PRODUCTION_CAPITAL_CAMPAIGN.id,
  }
}

/**
 * Highest fully valid level for one leader's eligible leg totals.
 * Upper band limits are labels only: extra shares can still qualify a lower level.
 * @param {number[]} legShares
 */
export function evaluateVeinLevel(legShares) {
  const legs = (legShares || [])
    .map((n) => Number(n) || 0)
    .filter((n) => n > 0)
    .sort((a, b) => b - a)
  const total = legs.reduce((sum, n) => sum + n, 0)
  let awarded = null
  for (const level of VEIN_ACCORD_LEVELS) {
    if (total + 1e-9 < level.minShares) continue
    if (legs.length < level.minLegs) continue
    const strongest = legs[0] || 0
    if (total > 0 && strongest / total > level.maxLegShare + 1e-9) continue
    if (level.minLegOfLevel > 0) {
      const floor = level.minShares * level.minLegOfLevel
      const required = legs.slice(0, level.minLegs)
      if (required.some((n) => n + 1e-9 < floor)) continue
    }
    if (!awarded || level.level > awarded.level) awarded = level
  }
  return {
    total,
    producingLegs: legs.length,
    strongest: legs[0] || 0,
    level: awarded ? awarded.level : 0,
    rate: awarded ? awarded.rate : 0,
  }
}

function ancestors(buyerId, parentOf) {
  const list = []
  const seen = new Set()
  let current = parentOf.get(buyerId)
  while (current && !seen.has(current)) {
    seen.add(current)
    list.push(current)
    current = parentOf.get(current)
  }
  return list
}

function legOf(leaderId, buyerId, parentOf) {
  const seen = new Set()
  let current = buyerId
  while (current && !seen.has(current)) {
    seen.add(current)
    const parent = parentOf.get(current)
    if (!parent) return null
    if (parent === leaderId) return current
    current = parent
  }
  return null
}

function mapOf(store, id) {
  if (!store.has(id)) store.set(id, new Map())
  return store.get(id)
}

function addShares(store, leaderId, legId, amount) {
  const legs = mapOf(store, leaderId)
  legs.set(legId, (legs.get(legId) || 0) + amount)
}

/**
 * Replay paid purchases in time order across pool-team leaders.
 * A leader's own purchase is not counted. Once a downline pool leader
 * qualifies, that organisation's eligible shares are removed from uplines
 * who have not already qualified. An upline who qualified earlier keeps
 * that qualification.
 *
 * @param {{ leaderIds: number[], parentOf: Map<number, number>, purchases: { id: string, userId: number, paidShares: number, at: string }[] }} input
 */
export function replayVeinAccord({ leaderIds, parentOf, purchases }) {
  const selected = new Set((leaderIds || []).map((id) => Number(id)))
  const eligible = new Map()
  const genealogy = new Map()
  const flow = new Map()
  const qualified = new Map()

  const ordered = [...(purchases || [])].sort((a, b) => {
    const delta = Date.parse(a.at) - Date.parse(b.at)
    if (delta) return delta
    return String(a.id).localeCompare(String(b.id))
  })

  for (const purchase of ordered) {
    const shares = Number(purchase.paidShares) || 0
    if (shares <= 0) continue
    const buyerId = Number(purchase.userId)
    const chain = ancestors(buyerId, parentOf)
    const selectedOnPath = chain.filter((id) => selected.has(id))
    let blocked = false

    for (let index = 0; index < selectedOnPath.length; index += 1) {
      const leaderId = selectedOnPath[index]
      const legId = legOf(leaderId, buyerId, parentOf)
      if (!legId) continue
      addShares(genealogy, leaderId, legId, shares)
      if (blocked) continue

      addShares(eligible, leaderId, legId, shares)
      for (let below = 0; below < index; below += 1) {
        const downlineId = selectedOnPath[below]
        const bucket = mapOf(flow, leaderId)
        const prev = bucket.get(downlineId) || { legId, shares: 0 }
        prev.shares += shares
        prev.legId = legId
        bucket.set(downlineId, prev)
      }

      if (qualified.has(leaderId)) {
        const next = evaluateVeinLevel([...(eligible.get(leaderId)?.values() || [])])
        const prev = qualified.get(leaderId)
        if (next.level > prev.level) {
          qualified.set(leaderId, {
            ...prev,
            level: next.level,
            rate: next.rate,
            paidShares: next.total,
          })
        }
        blocked = true
        continue
      }

      const result = evaluateVeinLevel([...(eligible.get(leaderId)?.values() || [])])
      if (!result.level) continue

      qualified.set(leaderId, {
        level: result.level,
        rate: result.rate,
        at: purchase.at,
        paidShares: result.total,
      })
      blocked = true
      for (const uplineId of selectedOnPath.slice(index + 1)) {
        if (qualified.has(uplineId)) continue
        const passed = flow.get(uplineId)?.get(leaderId)
        if (!passed) continue
        const legs = eligible.get(uplineId)
        if (!legs) continue
        const left = (legs.get(passed.legId) || 0) - passed.shares
        if (left > 1e-9) legs.set(passed.legId, left)
        else legs.delete(passed.legId)
        flow.get(uplineId).delete(leaderId)
      }
    }
  }

  return { eligible, genealogy, qualified }
}

export function leaderVeinSnapshot(replay, leaderId, directLegs) {
  const id = Number(leaderId)
  const eligibleLegs = replay.eligible.get(id) || new Map()
  const genealogyLegs = replay.genealogy.get(id) || new Map()
  const legs = (directLegs || []).map((leg) => ({
    userId: Number(leg.userId),
    username: leg.username || '',
    teamName: leg.teamName || null,
    eligibleShares: Number(eligibleLegs.get(Number(leg.userId)) || 0),
    genealogyShares: Number(genealogyLegs.get(Number(leg.userId)) || 0),
  }))
  const qualification = evaluateVeinLevel(legs.map((leg) => leg.eligibleShares))
  const locked = replay.qualified.get(id) || null
  const genealogyTotal = legs.reduce((sum, leg) => sum + leg.genealogyShares, 0)
  return {
    hasPoolTeam: true,
    legs,
    genealogyShares: genealogyTotal,
    eligibleShares: qualification.total,
    producingLegs: qualification.producingLegs,
    level: locked ? locked.level : qualification.level,
    rate: locked ? locked.rate : qualification.rate,
    qualifiedAt: locked ? locked.at : null,
  }
}
